"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Zap, MapPin, Star, Code as Code2, ChartBarBig as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  id: "01",
  title: "1. Comprehensive SEO & Technical Audit",
  summary: "I personally analyze your site's crawlability, indexing gaps, site architecture, and current rankings to uncover quick wins and core technical blockers.",
  deliverable: "Full technical roadmap & prioritized fix list",
  icon: Search
}, {
  id: "02",
  title: "2. On-Page & Content Optimization",
  summary: "I refine your site structure, align existing pages with search intent, and optimize metadata, target headings, and content depth for high-value keywords.",
  deliverable: "Fully optimized key conversion pages",
  icon: Zap
}, {
  id: "03",
  title: "3. Local & Authority Citations",
  summary: "For targeted markets, I build consistent NAP citations and local signals to ensure search engines accurately associate your business with key geographic hubs.",
  deliverable: "Structured business directory & authority profile",
  icon: MapPin
}, {
  id: "04",
  title: "4. Review & Reputation Strategy",
  summary: "I design legitimate review acquisition workflows that build user trust, elevate local Map Pack rankings, and convert casual traffic into direct inquiries.",
  deliverable: "Automated feedback & trust enhancement plan",
  icon: Star
}, {
  id: "05",
  title: "5. Structured Data & Schema Implementation",
  summary: "I deploy customized JSON-LD Schema markup across your content, ensuring search engines and AI engines clearly parse your entities, services, and authority.",
  deliverable: "Advanced Schema integration & snippet readiness",
  icon: Code2
}, {
  id: "06",
  title: "6. Transparent Monthly Reporting",
  summary: "Working directly with me means zero agency fluff. You get clear monthly updates on keyword visibility, organic traffic growth, and direct conversion impact.",
  deliverable: "Executive dashboard & monthly strategy call",
  icon: BarChart3
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const stepVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="stepbystepauditprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold border border-secondary">
            My Proven Methodology
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            Step-by-Step SEO Audit &amp; Growth Process
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When you hire me, you work directly with me—no account managers or automated templates. Here is the structured, step-by-step strategy I use to drive organic growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.id} data-index={i} variants={stepVariants} className="relative flex flex-col h-full">
                <Card className="bg-card border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col h-full relative overflow-hidden rounded-xl">
                  <div className="h-1.5 w-full bg-secondary" />

                  <CardHeader className="pt-6 pb-4 px-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center size-10 rounded-lg bg-primary text-primary-foreground font-bold text-sm tracking-wider shadow-xs">
                        {step.id}
                      </span>
                      <div className="p-2.5 rounded-lg bg-muted text-primary">
                        <IconComponent className="size-5" />
                      </div>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground font-serif">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {step.summary}
                    </CardDescription>

                    <div className="mt-auto pt-4 border-t border-border/60 flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-foreground">
                        <span className="text-muted-foreground">Deliverable: </span>
                        {step.deliverable}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 md:mt-20 rounded-2xl bg-muted/60 border border-border p-8 md:p-12 text-center max-w-4xl mx-auto shadow-sm">
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-4">
            Ready to audit your site&apos;s search potential?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8 text-base">
            Book a free, no-obligation consultation with me. I will review your current search visibility and outline direct opportunities for growth.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto font-medium" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium" asChild>
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}